/**
 * Admin -> Templates list (Phase 30).
 *
 * Shows all registered document templates. Enabled toggle writes directly
 * to DDB (no PR flow — templates are not git-gated).
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  listAdminTemplates,
  updateAdminTemplate,
} from "../services/adminTemplates.js";
import { useStore } from "../store/useStore.js";
import ErrorBubble from "../components/ErrorBubble.jsx";
import LoadingSkeleton from "../components/LoadingSkeleton.jsx";

export default function AdminTemplatesList() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const addToast = useStore((s) => s.addToast);

  const load = useCallback(() => {
    setLoading(true);
    return listAdminTemplates()
      .then((data) => {
        setTemplates(data.templates || []);
        setError(null);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function handleToggle(templateId, next) {
    setTogglingId(templateId);
    setTemplates((list) =>
      list.map((t) =>
        t.templateId === templateId ? { ...t, enabled: next } : t,
      ),
    );
    try {
      await updateAdminTemplate(templateId, { enabled: next });
      addToast({
        type: "success",
        message: `Template ${next ? "enabled" : "disabled"}.`,
      });
      setError(null);
    } catch (err) {
      setError(err.message);
      load();
    } finally {
      setTogglingId(null);
    }
  }

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <h1>Templates</h1>
        <Link to="/admin/templates/new" className="btn btn-primary">
          New template
        </Link>
      </div>

      <p className="admin-page__subtext">
        Document templates used by the pipeline to generate deliverables.
        Changes write directly — no PR required.
      </p>

      {error && <ErrorBubble message={error} />}
      {loading ? (
        <LoadingSkeleton variant="line" />
      ) : templates.length === 0 ? (
        <p className="admin-page__empty">
          No templates registered. Seed the database first.
        </p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Template ID</th>
              <th>Format</th>
              <th>Applicable Agents</th>
              <th>Enabled</th>
              <th>Version</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {templates.map((t) => (
              <tr key={t.templateId}>
                <td>
                  <Link
                    to={`/admin/templates/${encodeURIComponent(t.templateId)}/edit`}
                  >
                    {t.name}
                  </Link>
                </td>
                <td>
                  <code>{t.templateId}</code>
                </td>
                <td>
                  <span className="admin-badge">{t.format}</span>
                </td>
                <td>
                  {(t.applicableAgents || []).map((a) => (
                    <span key={a} className="admin-badge">
                      {a}
                    </span>
                  ))}
                </td>
                <td>
                  <label className="admin-toggle">
                    <input
                      type="checkbox"
                      checked={Boolean(t.enabled)}
                      disabled={togglingId === t.templateId}
                      onChange={(e) =>
                        handleToggle(t.templateId, e.target.checked)
                      }
                      aria-label={`${t.enabled ? "Disable" : "Enable"} ${t.name}`}
                    />
                    <span>{t.enabled ? "Enabled" : "Disabled"}</span>
                  </label>
                </td>
                <td>{t.version ?? "\u2014"}</td>
                <td>
                  <Link
                    to={`/admin/templates/${encodeURIComponent(t.templateId)}/edit`}
                    className="admin-action-link"
                  >
                    Edit
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
